import { useRef, useState } from "react";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { useLocation, useParams } from "wouter";
import { Logo } from "../design/Logo";
import { NAME_COLORS } from "../design/tokens";
import { t, fmt } from "../locale";
import { useAuth } from "../stores/auth";
import { useGame, RoomExit } from "../stores/game";
import { ROOM_CODE_LENGTH } from "@badorbad/shared";

/**
 * Why the player is looking at this screen rather than a room. Partial on
 * purpose — leaving on your own needs no explanation, and announcing one would
 * imply something went wrong.
 */
const EXIT_NOTICE: Partial<Record<RoomExit, string>> = {
  [RoomExit.Kicked]: t.connection.kicked,
  [RoomExit.Cancelled]: t.connection.roomGone,
  [RoomExit.OpenedElsewhere]: t.connection.openedElsewhere,
  [RoomExit.Gone]: t.connection.roomGone,
};

const HIGHLIGHT_COLOR = NAME_COLORS[1];

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string;

export const Landing = () => {
  const [, navigate] = useLocation();
  const params = useParams<{ code?: string }>();
  const prefilled = params.code ?? "";

  const [code, setCode] = useState(prefilled.toUpperCase());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const turnstileRef = useRef<TurnstileInstance | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const session = useAuth((s) => s.session);
  const signInAnonymously = useAuth((s) => s.signInAnonymously);
  const setPendingCode = useGame((s) => s.setPendingCode);
  const inRoom = useGame((s) => s.phase !== null);
  const exit = useGame((s) => s.exit);
  const clearExit = useGame((s) => s.clearExit);

  const ensureSession = async (): Promise<boolean> => {
    if (session) return true;

    if (!captchaToken) {
      setError(t.landing.verifyFailed);
      return false;
    }

    await signInAnonymously(captchaToken);
    setCaptchaToken(null);
    turnstileRef.current?.reset();

    const authError = useAuth.getState().error;
    if (authError) {
      setError(authError);
      return false;
    }
    return true;
  };

  const handleCreate = async () => {
    setBusy(true);
    setError(null);
    clearExit();
    const ok = await ensureSession();
    if (!ok) {
      setBusy(false);
      return;
    }
    setPendingCode("");
    navigate("/join");
    setBusy(false);
  };

  const handleJoin = async () => {
    const trimmed = code.trim().toUpperCase();
    if (trimmed.length !== ROOM_CODE_LENGTH) {
      setError(fmt(t.landing.invalidCode, ROOM_CODE_LENGTH));
      return;
    }
    setBusy(true);
    setError(null);
    clearExit();
    const ok = await ensureSession();
    if (!ok) {
      setBusy(false);
      return;
    }
    setPendingCode(trimmed);
    navigate("/join");
    setBusy(false);
  };

  const entryReady = Boolean(session || captchaToken);

  return (
    <div className="mx-auto flex min-h-full max-w-md flex-col px-5">
      <div className="flex flex-1 flex-col items-center justify-center gap-8 py-12">
        <div className="flex flex-col items-center gap-1 text-center">
          <Logo size={80} rounded={12} />
          <p className="mt-3 text-lg font-bold uppercase tracking-tight">
            {t.app.pitchLine1}
            <br />
            {t.app.pitchLine2}
          </p>
          <p className="text-sm text-ink-dim">
            {t.app.pitchLine3.split("%s")[0]}
            <span style={{ color: HIGHLIGHT_COLOR }}>{t.app.pitchHighlight}</span>
            {t.app.pitchLine3.split("%s")[1]}
          </p>
        </div>

        <div className="flex w-full flex-col gap-3">
          {/*
            A room outlives the screen you left it on, so someone who navigates
            back here is still in it. Offering "create a room" would be offering
            the one thing they cannot do — the server refuses a second room —
            and it hid the fact that they were in a room at all, which is why
            "you can't do that while you're in a room" read as a bug on the
            account screens.
          */}
          {inRoom ? (
            <button
              className="w-full bg-side-a px-4 py-4 text-base font-bold uppercase tracking-tight text-bg"
              onClick={() => navigate("/room")}
            >
              {t.landing.backToRoom}
            </button>
          ) : (
            <button
              className="w-full bg-side-a px-4 py-4 text-base font-bold uppercase tracking-tight text-bg disabled:cursor-not-allowed disabled:bg-side-b"
              disabled={busy || !entryReady}
              onClick={handleCreate}
            >
              {t.landing.createRoom}
            </button>
          )}

          <div className="flex items-center gap-3 text-xs text-ink-dim">
            <span className="h-px flex-1 bg-surface" />
            <span>{t.landing.joinRoom}</span>
            <span className="h-px flex-1 bg-surface" />
          </div>

          <input
            className="w-full bg-surface px-4 py-3 text-center text-base uppercase tracking-widest text-ink placeholder:text-ink-dim"
            placeholder="ROOM CODE"
            maxLength={ROOM_CODE_LENGTH}
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleJoin();
            }}
          />

          <button
            className="w-full bg-side-a px-4 py-4 text-base font-bold uppercase tracking-tight text-bg disabled:cursor-not-allowed disabled:bg-side-b"
            disabled={
              busy ||
              !entryReady ||
              code.trim().length !== ROOM_CODE_LENGTH
            }
            onClick={handleJoin}
          >
            {t.landing.joinRoom}
          </button>

          {!session && SITE_KEY && (
            <div className="flex justify-center">
              <Turnstile
                ref={turnstileRef}
                siteKey={SITE_KEY}
                options={{ size: "normal" }}
                onSuccess={(token) => {
                  setCaptchaToken(token);
                  setError(null);
                }}
                onError={() => {
                  setCaptchaToken(null);
                  setError(t.landing.verifyFailed);
                }}
                onExpire={() => {
                  setCaptchaToken(null);
                  turnstileRef.current?.reset();
                }}
              />
            </div>
          )}
        </div>

        {(error ?? (exit ? EXIT_NOTICE[exit] : undefined)) && (
          <p className="text-center text-sm text-side-b">
            {error ?? (exit ? EXIT_NOTICE[exit] : undefined)}
          </p>
        )}
      </div>

      <footer className="flex flex-col items-center gap-3 pb-8 text-xs text-ink-dim">
        <div className="flex gap-4">
          <button onClick={() => navigate("/how-to-play")} className="min-h-0 min-w-0">
            {t.landing.howToPlay}
          </button>
          {session?.user.email ? (
            <button onClick={() => navigate("/auth/account")} className="min-h-0 min-w-0">
              {t.auth.account}
            </button>
          ) : (
            <>
              <button onClick={() => navigate("/auth/sign-in")} className="min-h-0 min-w-0">
                {t.landing.signIn}
              </button>
              <button onClick={() => navigate("/auth/register")} className="min-h-0 min-w-0">
                {t.landing.register}
              </button>
            </>
          )}
        </div>
        {/* Reachable from the landing screen because that is the one page every
            player passes through, and the imprint has to be findable without
            already being in a room. */}
        <div className="flex gap-4">
          <button onClick={() => navigate("/privacy")} className="min-h-0 min-w-0">
            {t.privacy.title}
          </button>
          <button onClick={() => navigate("/imprint")} className="min-h-0 min-w-0">
            {t.imprint.title}
          </button>
        </div>
        <p className="text-center text-[10px]">{t.app.adultsOnly}</p>
      </footer>

    </div>
  );
};
